<template>
    <transition name="msgbox-fade">
        <div
            class="iu-message-box__wrapper"
            tabindex="-1"
            v-show="visible"
            @mousedown.self="handleWrapperClick"
            role="dialog"
            aria-modal="true"
            :aria-label="title || 'dialog'"
        >
            <div class="iu-message-box" :class="[customClass, center && 'iu-message-box--center']">
                <div class="iu-message-box__header" v-if="title !== null">
                    <div class="iu-message-box__title">
                        <div :class="['iu-message-box__status', icon]" v-if="icon && center"></div>
                        <span>{{ title }}</span>
                    </div>
                    <button
                        type="button"
                        class="iu-message-box__headerbtn"
                        aria-label="Close"
                        v-if="showClose"
                        @click="handleAction(distinguishCancelAndClose ? 'close' : 'cancel')"
                        @keydown.enter="handleAction(distinguishCancelAndClose ? 'close' : 'cancel')"
                    >
                        <i class="iu-message-box__close iu-icon-close"></i>
                    </button>
                </div>
                <div class="iu-message-box__content">
                    <div class="iu-message-box__container">
                        <div :class="['iu-message-box__status', icon]" v-if="icon && !center && message !== ''"></div>
                        <div class="iu-message-box__message" v-if="message !== ''">
                            <slot>
                                <p v-if="!dangerouslyUseHTMLString">{{ message }}</p>
                                <p v-else v-html="message"></p>
                            </slot>
                        </div>
                    </div>
                    <div class="iu-message-box__input" v-show="showInput">
                        <iu-input
                            v-model="inputValue"
                            :type="inputType"
                            @keydown.enter.native="handleInputEnter"
                            :placeholder="inputPlaceholder"
                            ref="input"
                        ></iu-input>
                        <div
                            class="iu-message-box__errormsg"
                            :style="{ visibility: !!editorErrorMessage ? 'visible' : 'hidden' }"
                        >
                            {{ editorErrorMessage }}
                        </div>
                    </div>
                </div>
                <div class="iu-message-box__btns">
                    <iu-button
                        :loading="cancelButtonLoading"
                        :class="[cancelButtonClasses]"
                        v-if="showCancelButton"
                        :round="roundButton"
                        size="small"
                        @click.native="handleAction('cancel')"
                        @keydown.enter="handleAction('cancel')"
                    >
                        {{ cancelButtonText || 'Cancel' }}
                    </iu-button>
                    <iu-button
                        :loading="confirmButtonLoading"
                        ref="confirm"
                        :class="[confirmButtonClasses]"
                        v-show="showConfirmButton"
                        :round="roundButton"
                        size="small"
                        @click.native="handleAction('confirm')"
                        @keydown.enter="handleAction('confirm')"
                    >
                        {{ confirmButtonText || 'OK' }}
                    </iu-button>
                </div>
            </div>
        </div>
    </transition>
</template>

<script type="text/babel">
import Popup from '../../../utils/popup';
import Locale from '../../../utils/locale';
import IuInput from '../../Input';
import IuButton from '../../Button';

import { addClass, removeClass } from '../../../utils/dom';
import Dialog from '../../../utils/aria-dialog';
let messageBox;
let typeMap = {
    success: 'success',
    info: 'info',
    warning: 'warning',
    error: 'error',
};
export default {
    mixins: [Popup, Locale],
    props: {
        modal: {
            default: true,
        },
        lockScroll: {
            default: true,
        },
        showClose: {
            type: Boolean,
            default: true,
        },
        closeOnClickModal: {
            default: true,
        },
        closeOnPressEscape: {
            default: true,
        },
        closeOnHashChange: {
            default: true,
        },
        center: {
            default: false,
            type: Boolean,
        },
        roundButton: {
            default: false,
            type: Boolean,
        },
    },
    components: {
        IuInput,
        IuButton,
    },
    computed: {
        icon() {
            const { type, iconClass } = this;
            return iconClass || (type && typeMap[type] ? `iu-icon-${typeMap[type]}` : '');
        },
        confirmButtonClasses() {
            return `iu-button--primary ${this.confirmButtonClass}`;
        },
        cancelButtonClasses() {
            return `${this.cancelButtonClass}`;
        },
    },
    methods: {
        getSafeClose() {
            const currentId = this.uid;
            return () => {
                this.$nextTick(() => {
                    if (currentId === this.uid) this.doClose();
                });
            };
        },
        doClose() {
            if (!this.visible) return;
            this.visible = false;
            this._closing = true;
            this.onClose && this.onClose();
            messageBox.closeDialog(); // 解绑
            if (this.lockScroll) {
                setTimeout(this.restoreBodyStyle, 200);
            }
            this.opened = false;
            this.doAfterClose();
            setTimeout(() => {
                if (this.action) this.callback(this.action, this);
            });
        },
        handleWrapperClick() {
            if (this.closeOnClickModal) {
                this.handleAction(this.distinguishCancelAndClose ? 'close' : 'cancel');
            }
        },
        handleInputEnter() {
            if (this.inputType !== 'textarea') {
                return this.handleAction('confirm');
            }
        },
        handleAction(action) {
            if (this.$type === 'prompt' && action === 'confirm' && !this.validate()) {
                return;
            }
            this.action = action;
            if (typeof this.beforeClose === 'function') {
                this.close = this.getSafeClose();
                this.beforeClose(action, this, this.close);
            } else {
                this.doClose();
            }
        },
        validate() {
            if (this.$type === 'prompt') {
                const inputPattern = this.inputPattern;
                if (inputPattern && !inputPattern.test(this.inputValue || '')) {
                    this.editorErrorMessage = this.inputErrorMessage || 'Illegal input';
                    addClass(this.getInputElement(), 'invalid');
                    return false;
                }
                const inputValidator = this.inputValidator;
                if (typeof inputValidator === 'function') {
                    const validateResult = inputValidator(this.inputValue);
                    if (validateResult === false) {
                        this.editorErrorMessage = this.inputErrorMessage || 'Illegal input';
                        addClass(this.getInputElement(), 'invalid');
                        return false;
                    }
                    if (typeof validateResult === 'string') {
                        this.editorErrorMessage = validateResult;
                        addClass(this.getInputElement(), 'invalid');
                        return false;
                    }
                }
            }
            this.editorErrorMessage = '';
            removeClass(this.getInputElement(), 'invalid');
            return true;
        },
        getFirstFocus() {
            const btn = this.$el.querySelector('.iu-message-box__btns .iu-button');
            const title = this.$el.querySelector('.iu-message-box__btns .iu-message-box__title');
            return btn || title;
        },
        getInputElement() {
            const inputRefs = this.$refs.input.$refs;
            return inputRefs.input || inputRefs.textarea;
        },
        handleClose() {
            this.handleAction('close');
        },
    },
    watch: {
        inputValue: {
            immediate: true,
            handler(val) {
                this.$nextTick(() => {
                    if (this.$type === 'prompt' && val !== null) {
                        this.validate();
                    }
                });
            },
        },
        visible(val) {
            if (val) {
                this.uid++;
                if (this.$type === 'alert' || this.$type === 'confirm') {
                    this.$nextTick(() => {
                        this.$refs.confirm.$el.focus();
                    });
                }
                this.focusAfterClosed = document.activeElement;
                messageBox = new Dialog(this.$el, this.focusAfterClosed, this.getFirstFocus());
            }
            // prompt
            if (this.$type !== 'prompt') return;
            if (val) {
                setTimeout(() => {
                    if (this.$refs.input && this.$refs.input.$el) {
                        this.getInputElement().focus();
                    }
                }, 500);
            } else {
                this.editorErrorMessage = '';
                removeClass(this.getInputElement(), 'invalid');
            }
        },
    },
    mounted() {
        this.$nextTick(() => {
            if (this.closeOnHashChange) {
                window.addEventListener('hashchange', this.close);
            }
        });
    },
    beforeDestroy() {
        if (this.closeOnHashChange) {
            window.removeEventListener('hashchange', this.close);
        }
        setTimeout(() => {
            messageBox.closeDialog();
        });
    },
    data() {
        return {
            uid: 1,
            title: undefined,
            message: '',
            type: '',
            iconClass: '',
            customClass: '',
            showInput: false,
            inputValue: null,
            inputPlaceholder: '',
            inputType: 'text',
            inputPattern: null,
            inputValidator: null,
            inputErrorMessage: '',
            showConfirmButton: true,
            showCancelButton: false,
            action: '',
            confirmButtonText: '',
            cancelButtonText: '',
            confirmButtonLoading: false,
            cancelButtonLoading: false,
            confirmButtonClass: '',
            confirmButtonDisabled: false,
            cancelButtonClass: '',
            editorErrorMessage: null,
            callback: null,
            dangerouslyUseHTMLString: false,
            focusAfterClosed: null,
            isOnComposition: false,
            distinguishCancelAndClose: false,
        };
    },
};
</script>
<style scope>
.iu-button-group > .iu-button.is-active,
.iu-button-group > .iu-button.is-disabled,
.iu-button-group > .iu-button:active,
.iu-button-group > .iu-button:focus,
.iu-button-group > .iu-button:hover {
    z-index: 1;
}
.iu-button,
.iu-input__inner {
    -webkit-appearance: none;
    outline: 0;
}
.iu-message-box,
.iu-popup-parent--hidden {
    overflow: hidden;
}
.v-modal-enter {
    -webkit-animation: v-modal-in 0.2s ease;
    animation: v-modal-in 0.2s ease;
}
.v-modal-leave {
    -webkit-animation: v-modal-out 0.2s ease forwards;
    animation: v-modal-out 0.2s ease forwards;
}
@-webkit-keyframes v-modal-in {
    0% {
        opacity: 0;
    }
}
@keyframes v-modal-in {
    0% {
        opacity: 0;
    }
}
@-webkit-keyframes v-modal-out {
    100% {
        opacity: 0;
    }
}
@keyframes v-modal-out {
    100% {
        opacity: 0;
    }
}
.v-modal {
    position: fixed;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    opacity: 0.5;
    background: #000;
}
.iu-button {
    display: inline-block;
    line-height: 1;
    white-space: nowrap;
    cursor: pointer;
    background: #fff;
    border: 1px solid #dcdfe6;
    color: #606266;
    text-align: center;
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
    margin: 0;
    -webkit-transition: 0.1s;
    transition: 0.1s;
    font-weight: 500;
    -moz-user-select: none;
    -webkit-user-select: none;
    -ms-user-select: none;
    padding: 12px 20px;
    font-size: 14px;
    border-radius: 4px;
}
.iu-button + .iu-button {
    margin-left: 10px;
}
.iu-button:focus,
.iu-button:hover {
    color: #409eff;
    border-color: #c6e2ff;
    background-color: #ecf5ff;
}
.iu-button:active {
    color: #3a8ee6;
    border-color: #3a8ee6;
    outline: 0;
}
.iu-button::-moz-focus-inner {
    border: 0;
}
.iu-button [class*='iu-icon-'] + span {
    margin-left: 5px;
}
.iu-button.is-plain:focus,
.iu-button.is-plain:hover {
    background: #fff;
    border-color: #409eff;
    color: #409eff;
}
.iu-button.is-active,
.iu-button.is-plain:active {
    color: #3a8ee6;
    border-color: #3a8ee6;
}
.iu-button.is-plain:active {
    background: #fff;
    outline: 0;
}
.iu-button.is-disabled,
.iu-button.is-disabled:focus,
.iu-button.is-disabled:hover {
    color: #c0c4cc;
    cursor: not-allowed;
    background-image: none;
    background-color: #fff;
    border-color: #ebeef5;
}
.iu-button.is-disabled.iu-button--text {
    background-color: transparent;
}
.iu-button.is-disabled.is-plain,
.iu-button.is-disabled.is-plain:focus,
.iu-button.is-disabled.is-plain:hover {
    background-color: #fff;
    border-color: #ebeef5;
    color: #c0c4cc;
}
.iu-button.is-loading {
    position: relative;
    pointer-events: none;
}
.iu-button.is-loading:before {
    pointer-events: none;
    content: '';
    position: absolute;
    left: -1px;
    top: -1px;
    right: -1px;
    bottom: -1px;
    border-radius: inherit;
    background-color: rgba(255, 255, 255, 0.35);
}
.iu-button.is-round {
    border-radius: 20px;
    padding: 12px 23px;
}
.iu-button.is-circle {
    border-radius: 50%;
    padding: 12px;
}
.iu-button--primary {
    color: #fff;
    background-color: #409eff;
    border-color: #409eff;
}
.iu-button--primary:focus,
.iu-button--primary:hover {
    background: #66b1ff;
    border-color: #66b1ff;
    color: #fff;
}
.iu-button--primary.is-active,
.iu-button--primary:active {
    background: #3a8ee6;
    border-color: #3a8ee6;
    color: #fff;
}
.iu-button--primary:active {
    outline: 0;
}
.iu-button--primary.is-disabled,
.iu-button--primary.is-disabled:active,
.iu-button--primary.is-disabled:focus,
.iu-button--primary.is-disabled:hover {
    color: #fff;
    background-color: #a0cfff;
    border-color: #a0cfff;
}
.iu-button--primary.is-plain {
    color: #409eff;
    background: #ecf5ff;
    border-color: #b3d8ff;
}
.iu-button--primary.is-plain:focus,
.iu-button--primary.is-plain:hover {
    background: #409eff;
    border-color: #409eff;
    color: #fff;
}
.iu-button--primary.is-plain:active {
    background: #3a8ee6;
    border-color: #3a8ee6;
    color: #fff;
    outline: 0;
}
.iu-button--primary.is-plain.is-disabled,
.iu-button--primary.is-plain.is-disabled:active,
.iu-button--primary.is-plain.is-disabled:focus,
.iu-button--primary.is-plain.is-disabled:hover {
    color: #8cc5ff;
    background-color: #ecf5ff;
    border-color: #d9ecff;
}
.iu-button--success {
    color: #fff;
    background-color: #67c23a;
    border-color: #67c23a;
}
.iu-button--success:focus,
.iu-button--success:hover {
    background: #85ce61;
    border-color: #85ce61;
    color: #fff;
}
.iu-button--success.is-active,
.iu-button--success:active {
    background: #5daf34;
    border-color: #5daf34;
    color: #fff;
}
.iu-button--success:active {
    outline: 0;
}
.iu-button--success.is-disabled,
.iu-button--success.is-disabled:active,
.iu-button--success.is-disabled:focus,
.iu-button--success.is-disabled:hover {
    color: #fff;
    background-color: #b3e19d;
    border-color: #b3e19d;
}
.iu-button--success.is-plain {
    color: #67c23a;
    background: #f0f9eb;
    border-color: #c2e7b0;
}
.iu-button--success.is-plain:focus,
.iu-button--success.is-plain:hover {
    background: #67c23a;
    border-color: #67c23a;
    color: #fff;
}
.iu-button--success.is-plain:active {
    background: #5daf34;
    border-color: #5daf34;
    color: #fff;
    outline: 0;
}
.iu-button--success.is-plain.is-disabled,
.iu-button--success.is-plain.is-disabled:active,
.iu-button--success.is-plain.is-disabled:focus,
.iu-button--success.is-plain.is-disabled:hover {
    color: #a4da89;
    background-color: #f0f9eb;
    border-color: #e1f3d8;
}
.iu-button--warning {
    color: #fff;
    background-color: #e6a23c;
    border-color: #e6a23c;
}
.iu-button--warning:focus,
.iu-button--warning:hover {
    background: #ebb563;
    border-color: #ebb563;
    color: #fff;
}
.iu-button--warning.is-active,
.iu-button--warning:active {
    background: #cf9236;
    border-color: #cf9236;
    color: #fff;
}
.iu-button--warning:active {
    outline: 0;
}
.iu-button--warning.is-disabled,
.iu-button--warning.is-disabled:active,
.iu-button--warning.is-disabled:focus,
.iu-button--warning.is-disabled:hover {
    color: #fff;
    background-color: #f3d19e;
    border-color: #f3d19e;
}
.iu-button--warning.is-plain {
    color: #e6a23c;
    background: #fdf6ec;
    border-color: #f5dab1;
}
.iu-button--warning.is-plain:focus,
.iu-button--warning.is-plain:hover {
    background: #e6a23c;
    border-color: #e6a23c;
    color: #fff;
}
.iu-button--warning.is-plain:active {
    background: #cf9236;
    border-color: #cf9236;
    color: #fff;
    outline: 0;
}
.iu-button--warning.is-plain.is-disabled,
.iu-button--warning.is-plain.is-disabled:active,
.iu-button--warning.is-plain.is-disabled:focus,
.iu-button--warning.is-plain.is-disabled:hover {
    color: #f0c78a;
    background-color: #fdf6ec;
    border-color: #faecd8;
}
.iu-button--danger {
    color: #fff;
    background-color: #f56c6c;
    border-color: #f56c6c;
}
.iu-button--danger:focus,
.iu-button--danger:hover {
    background: #f78989;
    border-color: #f78989;
    color: #fff;
}
.iu-button--danger.is-active,
.iu-button--danger:active {
    background: #dd6161;
    border-color: #dd6161;
    color: #fff;
}
.iu-button--danger:active {
    outline: 0;
}
.iu-button--danger.is-disabled,
.iu-button--danger.is-disabled:active,
.iu-button--danger.is-disabled:focus,
.iu-button--danger.is-disabled:hover {
    color: #fff;
    background-color: #fab6b6;
    border-color: #fab6b6;
}
.iu-button--danger.is-plain {
    color: #f56c6c;
    background: #fef0f0;
    border-color: #fbc4c4;
}
.iu-button--danger.is-plain:focus,
.iu-button--danger.is-plain:hover {
    background: #f56c6c;
    border-color: #f56c6c;
    color: #fff;
}
.iu-button--danger.is-plain:active {
    background: #dd6161;
    border-color: #dd6161;
    color: #fff;
    outline: 0;
}
.iu-button--danger.is-plain.is-disabled,
.iu-button--danger.is-plain.is-disabled:active,
.iu-button--danger.is-plain.is-disabled:focus,
.iu-button--danger.is-plain.is-disabled:hover {
    color: #f9a7a7;
    background-color: #fef0f0;
    border-color: #fde2e2;
}
.iu-button--info {
    color: #fff;
    background-color: #909399;
    border-color: #909399;
}
.iu-button--info:focus,
.iu-button--info:hover {
    background: #a6a9ad;
    border-color: #a6a9ad;
    color: #fff;
}
.iu-button--info.is-active,
.iu-button--info:active {
    background: #82848a;
    border-color: #82848a;
    color: #fff;
}
.iu-button--info:active {
    outline: 0;
}
.iu-button--info.is-disabled,
.iu-button--info.is-disabled:active,
.iu-button--info.is-disabled:focus,
.iu-button--info.is-disabled:hover {
    color: #fff;
    background-color: #c8c9cc;
    border-color: #c8c9cc;
}
.iu-button--info.is-plain {
    color: #909399;
    background: #f4f4f5;
    border-color: #d3d4d6;
}
.iu-button--info.is-plain:focus,
.iu-button--info.is-plain:hover {
    background: #909399;
    border-color: #909399;
    color: #fff;
}
.iu-button--info.is-plain:active {
    background: #82848a;
    border-color: #82848a;
    color: #fff;
    outline: 0;
}
.iu-button--info.is-plain.is-disabled,
.iu-button--info.is-plain.is-disabled:active,
.iu-button--info.is-plain.is-disabled:focus,
.iu-button--info.is-plain.is-disabled:hover {
    color: #bcbec2;
    background-color: #f4f4f5;
    border-color: #e9e9eb;
}
.iu-button--text,
.iu-button--text.is-disabled,
.iu-button--text.is-disabled:focus,
.iu-button--text.is-disabled:hover,
.iu-button--text:active {
    border-color: transparent;
}
.iu-button--medium {
    padding: 10px 20px;
    font-size: 14px;
    border-radius: 4px;
}
.iu-button--mini,
.iu-button--small {
    font-size: 12px;
    border-radius: 3px;
}
.iu-button--medium.is-round {
    padding: 10px 20px;
}
.iu-button--medium.is-circle {
    padding: 10px;
}
.iu-button--small,
.iu-button--small.is-round {
    padding: 9px 15px;
}
.iu-button--small.is-circle {
    padding: 9px;
}
.iu-button--mini,
.iu-button--mini.is-round {
    padding: 7px 15px;
}
.iu-button--mini.is-circle {
    padding: 7px;
}
.iu-button--text {
    color: #409eff;
    background: 0 0;
    padding-left: 0;
    padding-right: 0;
}
.iu-button--text:focus,
.iu-button--text:hover {
    color: #66b1ff;
    border-color: transparent;
    background-color: transparent;
}
.iu-button--text:active {
    color: #3a8ee6;
    background-color: transparent;
}
.iu-button-group {
    display: inline-block;
    vertical-align: middle;
}
.iu-button-group::after,
.iu-button-group::before {
    display: table;
    content: '';
}
.iu-button-group::after {
    clear: both;
}
.iu-button-group > .iu-button {
    float: left;
    position: relative;
}
.iu-button-group > .iu-button + .iu-button {
    margin-left: 0;
}
.iu-button-group > .iu-button:first-child {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
}
.iu-button-group > .iu-button:last-child {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
}
.iu-button-group > .iu-button:first-child:last-child {
    border-radius: 4px;
}
.iu-button-group > .iu-button:first-child:last-child.is-round {
    border-radius: 20px;
}
.iu-button-group > .iu-button:first-child:last-child.is-circle {
    border-radius: 50%;
}
.iu-button-group > .iu-button:not(:first-child):not(:last-child) {
    border-radius: 0;
}
.iu-button-group > .iu-button:not(:last-child) {
    margin-right: -1px;
}
.iu-button-group > .iu-dropdown > .iu-button {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    border-left-color: rgba(255, 255, 255, 0.5);
}
.iu-button-group .iu-button--primary:first-child {
    border-right-color: rgba(255, 255, 255, 0.5);
}
.iu-button-group .iu-button--primary:last-child {
    border-left-color: rgba(255, 255, 255, 0.5);
}
.iu-button-group .iu-button--primary:not(:first-child):not(:last-child) {
    border-left-color: rgba(255, 255, 255, 0.5);
    border-right-color: rgba(255, 255, 255, 0.5);
}
.iu-button-group .iu-button--success:first-child {
    border-right-color: rgba(255, 255, 255, 0.5);
}
.iu-button-group .iu-button--success:last-child {
    border-left-color: rgba(255, 255, 255, 0.5);
}
.iu-button-group .iu-button--success:not(:first-child):not(:last-child) {
    border-left-color: rgba(255, 255, 255, 0.5);
    border-right-color: rgba(255, 255, 255, 0.5);
}
.iu-button-group .iu-button--warning:first-child {
    border-right-color: rgba(255, 255, 255, 0.5);
}
.iu-button-group .iu-button--warning:last-child {
    border-left-color: rgba(255, 255, 255, 0.5);
}
.iu-button-group .iu-button--warning:not(:first-child):not(:last-child) {
    border-left-color: rgba(255, 255, 255, 0.5);
    border-right-color: rgba(255, 255, 255, 0.5);
}
.iu-button-group .iu-button--danger:first-child {
    border-right-color: rgba(255, 255, 255, 0.5);
}
.iu-button-group .iu-button--danger:last-child {
    border-left-color: rgba(255, 255, 255, 0.5);
}
.iu-button-group .iu-button--danger:not(:first-child):not(:last-child) {
    border-left-color: rgba(255, 255, 255, 0.5);
    border-right-color: rgba(255, 255, 255, 0.5);
}
.iu-button-group .iu-button--info:first-child {
    border-right-color: rgba(255, 255, 255, 0.5);
}
.iu-button-group .iu-button--info:last-child {
    border-left-color: rgba(255, 255, 255, 0.5);
}
.iu-button-group .iu-button--info:not(:first-child):not(:last-child) {
    border-left-color: rgba(255, 255, 255, 0.5);
    border-right-color: rgba(255, 255, 255, 0.5);
}
.iu-textarea {
    position: relative;
    display: inline-block;
    width: 100%;
    vertical-align: bottom;
    font-size: 14px;
}
.iu-textarea__inner {
    display: block;
    resize: vertical;
    padding: 5px 15px;
    line-height: 1.5;
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
    width: 100%;
    font-size: inherit;
    color: #606266;
    background-color: #fff;
    background-image: none;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    -webkit-transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
    transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
}
.iu-textarea__inner::-webkit-input-placeholder {
    color: #c0c4cc;
}
.iu-textarea__inner:-ms-input-placeholder {
    color: #c0c4cc;
}
.iu-textarea__inner::-ms-input-placeholder {
    color: #c0c4cc;
}
.iu-textarea__inner::placeholder {
    color: #c0c4cc;
}
.iu-textarea__inner:hover {
    border-color: #c0c4cc;
}
.iu-textarea__inner:focus {
    outline: 0;
    border-color: #409eff;
}
.iu-textarea .iu-input__count {
    color: #909399;
    background: #fff;
    position: absolute;
    font-size: 12px;
    bottom: 5px;
    right: 10px;
}
.iu-textarea.is-disabled .iu-textarea__inner {
    background-color: #f5f7fa;
    border-color: #e4e7ed;
    color: #c0c4cc;
    cursor: not-allowed;
}
.iu-textarea.is-disabled .iu-textarea__inner::-webkit-input-placeholder {
    color: #c0c4cc;
}
.iu-textarea.is-disabled .iu-textarea__inner:-ms-input-placeholder {
    color: #c0c4cc;
}
.iu-textarea.is-disabled .iu-textarea__inner::-ms-input-placeholder {
    color: #c0c4cc;
}
.iu-textarea.is-disabled .iu-textarea__inner::placeholder {
    color: #c0c4cc;
}
.iu-textarea.is-exceed .iu-textarea__inner {
    border-color: #f56c6c;
}
.iu-textarea.is-exceed .iu-input__count {
    color: #f56c6c;
}
.iu-input {
    position: relative;
    font-size: 14px;
    display: inline-block;
    width: 100%;
}
.iu-input::-webkit-scrollbar {
    z-index: 11;
    width: 6px;
}
.iu-input::-webkit-scrollbar:horizontal {
    height: 6px;
}
.iu-input::-webkit-scrollbar-thumb {
    border-radius: 5px;
    width: 6px;
    background: #b4bccc;
}
.iu-input::-webkit-scrollbar-corner {
    background: #fff;
}
.iu-input::-webkit-scrollbar-track {
    background: #fff;
}
.iu-input::-webkit-scrollbar-track-piece {
    background: #fff;
    width: 6px;
}
.iu-input .iu-input__clear {
    color: #c0c4cc;
    font-size: 14px;
    cursor: pointer;
    -webkit-transition: color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
    transition: color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
}
.iu-input .iu-input__clear:hover {
    color: #909399;
}
.iu-input .iu-input__count {
    height: 100%;
    display: -webkit-inline-box;
    display: -ms-inline-flexbox;
    display: inline-flex;
    -webkit-box-align: center;
    -ms-flex-align: center;
    align-items: center;
    color: #909399;
    font-size: 12px;
}
.iu-input .iu-input__count .iu-input__count-inner {
    background: #fff;
    line-height: initial;
    display: inline-block;
    padding: 0 5px;
}
.iu-input__inner {
    background-color: #fff;
    background-image: none;
    border-radius: 4px;
    border: 1px solid #dcdfe6;
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
    color: #606266;
    display: inline-block;
    font-size: inherit;
    height: 40px;
    line-height: 40px;
    padding: 0 15px;
    -webkit-transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
    transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
    width: 100%;
}
.iu-input__prefix,
.iu-input__suffix {
    position: absolute;
    -webkit-transition: all 0.3s;
    text-align: center;
    height: 100%;
    color: #c0c4cc;
    top: 0;
}
.iu-input__inner::-webkit-input-placeholder {
    color: #c0c4cc;
}
.iu-input__inner:-ms-input-placeholder {
    color: #c0c4cc;
}
.iu-input__inner::-ms-input-placeholder {
    color: #c0c4cc;
}
.iu-input__inner::placeholder {
    color: #c0c4cc;
}
.iu-input__inner:hover {
    border-color: #c0c4cc;
}
.iu-input.is-active .iu-input__inner,
.iu-input__inner:focus {
    border-color: #409eff;
    outline: 0;
}
.iu-input__suffix {
    right: 5px;
    transition: all 0.3s;
    pointer-events: none;
}
.iu-input__suffix-inner {
    pointer-events: all;
}
.iu-input__prefix {
    left: 5px;
    transition: all 0.3s;
}
.iu-input__icon {
    height: 100%;
    width: 25px;
    text-align: center;
    -webkit-transition: all 0.3s;
    transition: all 0.3s;
    line-height: 40px;
}
.iu-input__icon:after {
    content: '';
    height: 100%;
    width: 0;
    display: inline-block;
    vertical-align: middle;
}
.iu-input__validateIcon {
    pointer-events: none;
}
.iu-input.is-disabled .iu-input__inner {
    background-color: #f5f7fa;
    border-color: #e4e7ed;
    color: #c0c4cc;
    cursor: not-allowed;
}
.iu-input.is-disabled .iu-input__inner::-webkit-input-placeholder {
    color: #c0c4cc;
}
.iu-input.is-disabled .iu-input__inner:-ms-input-placeholder {
    color: #c0c4cc;
}
.iu-input.is-disabled .iu-input__inner::-ms-input-placeholder {
    color: #c0c4cc;
}
.iu-input.is-disabled .iu-input__inner::placeholder {
    color: #c0c4cc;
}
.iu-input.is-disabled .iu-input__icon {
    cursor: not-allowed;
}
.iu-input.is-exceed .iu-input__inner {
    border-color: #f56c6c;
}
.iu-input.is-exceed .iu-input__suffix .iu-input__count {
    color: #f56c6c;
}
.iu-input--suffix .iu-input__inner {
    padding-right: 30px;
}
.iu-input--prefix .iu-input__inner {
    padding-left: 30px;
}
.iu-input--medium {
    font-size: 14px;
}
.iu-input--medium .iu-input__inner {
    height: 36px;
    line-height: 36px;
}
.iu-input--medium .iu-input__icon {
    line-height: 36px;
}
.iu-input--small {
    font-size: 13px;
}
.iu-input--small .iu-input__inner {
    height: 32px;
    line-height: 32px;
}
.iu-input--small .iu-input__icon {
    line-height: 32px;
}
.iu-input--mini {
    font-size: 12px;
}
.iu-input--mini .iu-input__inner {
    height: 28px;
    line-height: 28px;
}
.iu-input--mini .iu-input__icon {
    line-height: 28px;
}
.iu-input-group {
    line-height: normal;
    display: inline-table;
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
}
.iu-input-group > .iu-input__inner {
    vertical-align: middle;
    display: table-cell;
}
.iu-input-group__append,
.iu-input-group__prepend {
    background-color: #f5f7fa;
    color: #909399;
    vertical-align: middle;
    display: table-cell;
    position: relative;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    padding: 0 20px;
    width: 1px;
    white-space: nowrap;
}
.iu-input-group--prepend .iu-input__inner,
.iu-input-group__append {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
}
.iu-input-group--append .iu-input__inner,
.iu-input-group__prepend {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
}
.iu-input-group__append:focus,
.iu-input-group__prepend:focus {
    outline: 0;
}
.iu-input-group__append .iu-button,
.iu-input-group__append .iu-select,
.iu-input-group__prepend .iu-button,
.iu-input-group__prepend .iu-select {
    display: inline-block;
    margin: -10px -20px;
}
.iu-input-group__append button.iu-button,
.iu-input-group__append div.iu-select .iu-input__inner,
.iu-input-group__append div.iu-select:hover .iu-input__inner,
.iu-input-group__prepend button.iu-button,
.iu-input-group__prepend div.iu-select .iu-input__inner,
.iu-input-group__prepend div.iu-select:hover .iu-input__inner {
    border-color: transparent;
    background-color: transparent;
    color: inherit;
    border-top: 0;
    border-bottom: 0;
}
.iu-input-group__append .iu-button,
.iu-input-group__append .iu-input,
.iu-input-group__prepend .iu-button,
.iu-input-group__prepend .iu-input {
    font-size: inherit;
}
.iu-input-group__prepend {
    border-right: 0;
}
.iu-input-group__append {
    border-left: 0;
}
.iu-input-group--append .iu-select .iu-input.is-focus .iu-input__inner,
.iu-input-group--prepend .iu-select .iu-input.is-focus .iu-input__inner {
    border-color: transparent;
}
.iu-input__inner::-ms-clear {
    display: none;
    width: 0;
    height: 0;
}
.iu-message-box {
    display: inline-block;
    width: 420px;
    padding-bottom: 10px;
    vertical-align: middle;
    background-color: #fff;
    border-radius: 4px;
    border: 1px solid #ebeef5;
    font-size: 18px;
    -webkit-box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    text-align: left;
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
}
.iu-message-box__wrapper {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    text-align: center;
}
.iu-message-box__wrapper::after {
    content: '';
    display: inline-block;
    height: 100%;
    width: 0;
    vertical-align: middle;
}
.iu-message-box__header {
    position: relative;
    padding: 15px 15px 10px;
}
.iu-message-box__title {
    padding-left: 0;
    margin-bottom: 0;
    font-size: 18px;
    line-height: 1;
    color: #303133;
}
.iu-message-box__headerbtn {
    position: absolute;
    top: 15px;
    right: 15px;
    padding: 0;
    border: none;
    outline: 0;
    background: 0 0;
    font-size: 16px;
    cursor: pointer;
}
.iu-message-box__headerbtn .iu-message-box__close {
    color: #909399;
}
.iu-message-box__headerbtn:focus .iu-message-box__close,
.iu-message-box__headerbtn:hover .iu-message-box__close {
    color: #409eff;
}
.iu-message-box__content {
    padding: 10px 15px;
    color: #606266;
    font-size: 14px;
}
.iu-message-box__container {
    position: relative;
}
.iu-message-box__input {
    padding-top: 15px;
}
.iu-message-box__input input.invalid,
.iu-message-box__input input.invalid:focus {
    border-color: #f56c6c;
}
.iu-message-box__status {
    position: absolute;
    top: 50%;
    -webkit-transform: translateY(-50%);
    transform: translateY(-50%);
    font-size: 24px !important;
}
.iu-message-box__status::before {
    padding-left: 1px;
}
.iu-message-box__status + .iu-message-box__message {
    padding-left: 36px;
    padding-right: 12px;
}
.iu-message-box__status.iu-icon-success {
    color: #67c23a;
}
.iu-message-box__status.iu-icon-info {
    color: #909399;
}
.iu-message-box__status.iu-icon-warning {
    color: #e6a23c;
}
.iu-message-box__status.iu-icon-error {
    color: #f56c6c;
}
.iu-message-box__message {
    margin: 0;
}
.iu-message-box__message p {
    margin: 0;
    line-height: 24px;
}
.iu-message-box__errormsg {
    color: #f56c6c;
    font-size: 12px;
    min-height: 18px;
    margin-top: 2px;
}
.iu-message-box__btns {
    padding: 5px 15px 0;
    text-align: right;
}
.iu-message-box__btns button:nth-child(2) {
    margin-left: 10px;
}
.iu-message-box__btns-reverse {
    -webkit-box-orient: horizontal;
    -webkit-box-direction: reverse;
    -ms-flex-direction: row-reverse;
    flex-direction: row-reverse;
}
.iu-message-box--center {
    padding-bottom: 30px;
}
.iu-message-box--center .iu-message-box__header {
    padding-top: 30px;
}
.iu-message-box--center .iu-message-box__title {
    position: relative;
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-align: center;
    -ms-flex-align: center;
    align-items: center;
    -webkit-box-pack: center;
    -ms-flex-pack: center;
    justify-content: center;
}
.iu-message-box--center .iu-message-box__status {
    position: relative;
    top: auto;
    padding-right: 5px;
    text-align: center;
    -webkit-transform: translateY(-1px);
    transform: translateY(-1px);
}
.iu-message-box--center .iu-message-box__message {
    margin-left: 0;
}
.iu-message-box--center .iu-message-box__btns,
.iu-message-box--center .iu-message-box__content {
    text-align: center;
}
.iu-message-box--center .iu-message-box__content {
    padding-left: 27px;
    padding-right: 27px;
}
.msgbox-fade-enter-active {
    -webkit-animation: msgbox-fade-in 0.3s;
    animation: msgbox-fade-in 0.3s;
}
.msgbox-fade-leave-active {
    -webkit-animation: msgbox-fade-out 0.3s;
    animation: msgbox-fade-out 0.3s;
}
@-webkit-keyframes msgbox-fade-in {
    0% {
        -webkit-transform: translate3d(0, -20px, 0);
        transform: translate3d(0, -20px, 0);
        opacity: 0;
    }
    100% {
        -webkit-transform: translate3d(0, 0, 0);
        transform: translate3d(0, 0, 0);
        opacity: 1;
    }
}
@keyframes msgbox-fade-in {
    0% {
        -webkit-transform: translate3d(0, -20px, 0);
        transform: translate3d(0, -20px, 0);
        opacity: 0;
    }
    100% {
        -webkit-transform: translate3d(0, 0, 0);
        transform: translate3d(0, 0, 0);
        opacity: 1;
    }
}
@-webkit-keyframes msgbox-fade-out {
    0% {
        -webkit-transform: translate3d(0, 0, 0);
        transform: translate3d(0, 0, 0);
        opacity: 1;
    }
    100% {
        -webkit-transform: translate3d(0, -20px, 0);
        transform: translate3d(0, -20px, 0);
        opacity: 0;
    }
}
@keyframes msgbox-fade-out {
    0% {
        -webkit-transform: translate3d(0, 0, 0);
        transform: translate3d(0, 0, 0);
        opacity: 1;
    }
    100% {
        -webkit-transform: translate3d(0, -20px, 0);
        transform: translate3d(0, -20px, 0);
        opacity: 0;
    }
}
</style>
